import React, { useState, useEffect, useMemo } from 'react';
import { 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Box, 
  Typography,
  Checkbox,
  ListItemText,
  Chip,
  OutlinedInput,
  TextField,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { formatDate } from '../../utils/formatters';

/**
 * WorkItemsFilterPanel component for filtering work items
 * Shows only Epic Target Date and Team filters
 * @param {Object} props - Component props
 * @param {Function} props.onFilterChange - Callback when filters change
 * @param {Array} props.workItems - Array of work items to extract filter values from
 * @returns {JSX.Element} WorkItemsFilterPanel component
 */
const WorkItemsFilterPanel = ({ 
  onFilterChange, 
  workItems = [],
  initialSearchText = ''
}) => {
  // Filter states
  const [epicTargetDate, setEpicTargetDate] = useState([]);
  const [team, setTeam] = useState([]);
  const [state, setState] = useState([]);
  const [searchInput, setSearchInput] = useState(initialSearchText || ''); // Input value (what user types)
  const [searchText, setSearchText] = useState(initialSearchText || ''); // Actual search filter value (applied on Enter)
  
  // Update search when initialSearchText changes
  useEffect(() => {
    if (initialSearchText) {
      setSearchInput(initialSearchText);
      setSearchText(initialSearchText);
    }
  }, [initialSearchText]);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  
  // Extract unique epic target dates from work items
  const epicTargetDates = useMemo(() => {
    const uniqueDates = new Set();
    
    workItems.forEach(epic => {
      if (epic.targetDate) {
        const formattedDate = formatDate(epic.targetDate);
        uniqueDates.add(formattedDate);
      }
    });
    
    return Array.from(uniqueDates).sort();
  }, [workItems]);
  
  // Extract unique teams from work items (epic and feature level)
  const teams = useMemo(() => {
    const uniqueTeams = new Set();
    
    workItems.forEach(epic => {
      // Add epic team
      if (epic.team) {
        uniqueTeams.add(epic.team);
      }
      
      // Add feature teams
      if (epic.features && epic.features.length > 0) {
        epic.features.forEach(feature => {
          if (feature.team) {
            uniqueTeams.add(feature.team);
          }
        });
      }
    });
    
    return Array.from(uniqueTeams).sort();
  }, [workItems]);
  
  // Extract unique states from work items
  const states = useMemo(() => {
    const uniqueStates = new Set();
    
    workItems.forEach(epic => {
      if (epic.state) {
        uniqueStates.add(epic.state);
      }
    });
    
    return Array.from(uniqueStates).sort();
  }, [workItems]);
  
  // Create memoized filters object
  const filters = useMemo(() => ({
    ...(epicTargetDate.length > 0 && { epicTargetDate: epicTargetDate.join(',') }),
    ...(team.length > 0 && { team: team.join(',') }),
    ...(state.length > 0 && { state: state.join(',') }),
    ...(searchText.trim() && { searchText: searchText.trim() })
  }), [epicTargetDate, team, state, searchText]);
  
  // Apply filters when they change
  useEffect(() => {
    onFilterChange(filters);
  }, [filters, onFilterChange]);
  
  // Handle search input change (only updates input, not filter)
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle Enter key press to apply search
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchText(searchInput.trim());
    }
  };
  
  // Set search text from props if provided (e.g., from URL param)
  useEffect(() => {
    // This will be handled by WorkItemsView setting filters directly
  }, []);

  // Reset all filters
  const handleReset = () => {
    setEpicTargetDate([]);
    setTeam([]);
    setState([]);
    setSearchInput('');
    setSearchText('');
  };
  
  // Clear specific filter
  const handleClearFilter = (filterName) => {
    switch(filterName) {
      case 'epicTargetDate':
        setEpicTargetDate([]);
        break;
      case 'team':
        setTeam([]);
        break;
      case 'state':
        setState([]);
        break;
      default:
        break;
    }
  };
  
  return (
    <Paper style={{ padding: 16, marginBottom: 16 }}>
      <Box display="flex" alignItems="center" marginBottom={2}>
        <FilterIcon style={{ marginRight: 8 }} />
        <Typography variant="h6">Filters</Typography>
      </Box>
      
      <Grid container spacing={2} alignItems="center">
        {/* Search Text Input */}
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Search by ADO ID"
            placeholder="Enter ADO ID..."
            value={searchInput}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchKeyDown}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            }}
            disabled={loading}
          />
        </Grid>
        
        {/* Epic Target Date Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="epic-target-date-filter-label">Epic Target Date</InputLabel>
            <Select
              labelId="epic-target-date-filter-label"
              id="epic-target-date-filter"
              multiple
              value={epicTargetDate}
              onChange={(e) => setEpicTargetDate(e.target.value)}
              input={<OutlinedInput label="Epic Target Date" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={loading || epicTargetDates.length === 0}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 250,
                  },
                },
              }}
            >
              {epicTargetDate.length > 0 && (
                <MenuItem onClick={() => handleClearFilter('epicTargetDate')}>
                  <em>Clear Selection</em>
                </MenuItem>
              )}
              {epicTargetDates.length === 0 ? (
                <MenuItem disabled>
                  <ListItemText primary="No epic target dates found" />
                </MenuItem>
              ) : (
                epicTargetDates.map((dateOption) => (
                  <MenuItem key={dateOption} value={dateOption}>
                    <Checkbox checked={epicTargetDate.indexOf(dateOption) > -1} />
                    <ListItemText primary={dateOption} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Team Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="team-filter-label">Team</InputLabel>
            <Select
              labelId="team-filter-label"
              id="team-filter"
              multiple
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              input={<OutlinedInput label="Team" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={loading || teams.length === 0}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 250,
                  },
                },
              }}
            >
              {team.length > 0 && (
                <MenuItem onClick={() => handleClearFilter('team')}>
                  <em>Clear Selection</em>
                </MenuItem>
              )}
              {teams.length === 0 ? (
                <MenuItem disabled>
                  <ListItemText primary="No teams found" />
                </MenuItem>
              ) : (
                teams.map((teamOption) => (
                  <MenuItem key={teamOption} value={teamOption}>
                    <Checkbox checked={team.indexOf(teamOption) > -1} />
                    <ListItemText primary={teamOption} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        
        {/* State Filter */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel id="state-filter-label">State</InputLabel>
            <Select
              labelId="state-filter-label"
              id="state-filter"
              multiple
              value={state}
              onChange={(e) => setState(e.target.value)}
              input={<OutlinedInput label="State" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} size="small" />
                  ))}
                </Box>
              )}
              disabled={loading || states.length === 0}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 4.5 + 8,
                    width: 250,
                  },
                },
              }}
            >
              {state.length > 0 && (
                <MenuItem onClick={() => handleClearFilter('state')}>
                  <em>Clear Selection</em>
                </MenuItem>
              )}
              {states.length === 0 ? (
                <MenuItem disabled>
                  <ListItemText primary="No states found" />
                </MenuItem>
              ) : (
                states.map((stateOption) => (
                  <MenuItem key={stateOption} value={stateOption}>
                    <Checkbox checked={state.indexOf(stateOption) > -1} />
                    <ListItemText primary={stateOption} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
        
        {/* Clear Filters Button */}
        <Grid item xs={12} sm={6} md={3}>
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <Tooltip title="Clear Filters">
              <IconButton
                color="secondary"
                onClick={handleReset}
                disabled={loading || (epicTargetDate.length === 0 && team.length === 0 && state.length === 0 && !searchText.trim() && !searchInput.trim())}
                size="small"
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default WorkItemsFilterPanel;
